import { useEffect, useMemo, useRef, useState, type KeyboardEvent, type MouseEvent } from 'react';

import { DOC_PAGES, DOCS } from '../docs/content.tsx';
import { indexDocs, searchDocs, type DocHit } from '../lib/docsearch.ts';
import { useApp } from '../state.tsx';

const INDEX = indexDocs(DOC_PAGES);
const SECTION_OF = new Map(DOCS.flatMap((s) => s.pages.map((p) => [p.slug, s.label] as const)));
/** A shorter query matches everything */
const MIN_QUERY = 2;

/** Protocol documentation: sections in a sidebar, one page at a time, its headings listed on the right; a
 * search box above the sidebar replaces the page with the matching passages while a query is typed */
export function DocsPage() {
  const { route, go } = useApp();
  const page = DOC_PAGES.find((p) => p.slug === route.doc) ?? DOC_PAGES[0]!;
  const i = DOC_PAGES.indexOf(page);
  const prev = DOC_PAGES[i - 1];
  const next = DOC_PAGES[i + 1];
  const article = useRef<HTMLElement>(null);
  const input = useRef<HTMLInputElement>(null);
  /** A heading to scroll to once its page is on screen (a search result on another page) */
  const pending = useRef<{ slug: string; id: string }>(undefined);
  const [toc, setToc] = useState<{ id: string; text: string }[]>([]);
  const [query, setQuery] = useState('');
  const q = query.trim();
  const searching = q.length >= MIN_QUERY;
  const hits = useMemo(() => (searching ? searchDocs(INDEX, q) : []), [searching, q]);

  // headings come from the rendered page, so the outline never drifts from the content
  useEffect(() => {
    const hs = article.current?.querySelectorAll('h2[id]') ?? [];
    setToc([...hs].map((h) => ({ id: h.id, text: h.textContent ?? '' })));
    // on a phone the sidebar is a horizontal strip: bring the current page into view
    document.querySelector('.docs-nav a.is-on')?.scrollIntoView({ block: 'nearest', inline: 'center' });
    const target = pending.current;
    if (target && target.slug === page.slug && !searching) {
      pending.current = undefined;
      document.getElementById(target.id)?.scrollIntoView({ block: 'start' });
    } else {
      window.scrollTo(0, 0);
    }
  }, [page, searching]);

  // "/" or Ctrl+K (Cmd+K) focuses the search from anywhere on the page
  useEffect(() => {
    const on = (e: globalThis.KeyboardEvent) => {
      const t = e.target as HTMLElement | null;
      const typing = t != null && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.tagName === 'SELECT' || t.isContentEditable);
      const slash = e.key === '/' && !typing && !e.ctrlKey && !e.metaKey && !e.altKey;
      const k = e.key.toLowerCase() === 'k' && (e.ctrlKey || e.metaKey) && !e.altKey;
      if (!slash && !k) return;
      e.preventDefault();
      input.current?.focus();
      input.current?.select();
    };
    window.addEventListener('keydown', on);
    return () => window.removeEventListener('keydown', on);
  }, []);

  const open = (e: MouseEvent<HTMLAnchorElement>, hit: DocHit) => {
    // a modified click opens the page in a new tab, as any link
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
    e.preventDefault();
    pending.current = hit.entry.heading ? { slug: hit.entry.slug, id: hit.entry.heading.id } : undefined;
    setQuery('');
    go({ page: 'docs', doc: hit.entry.slug });
  };

  const onKey = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape') {
      setQuery('');
      input.current?.blur();
    } else if (e.key === 'Enter' && hits[0]) {
      const hit = hits[0];
      pending.current = hit.entry.heading ? { slug: hit.entry.slug, id: hit.entry.heading.id } : undefined;
      setQuery('');
      input.current?.blur();
      go({ page: 'docs', doc: hit.entry.slug });
    }
  };

  return (
    <div className="docs">
      <nav className="docs-nav" aria-label="Documentation">
        <form className="docs-search" role="search" onSubmit={(e) => e.preventDefault()}>
          <input
            ref={input}
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={onKey}
            placeholder="Search the docs"
            aria-label="Search the documentation"
            autoComplete="off"
            spellCheck={false}
          />
          <kbd aria-hidden="true">/</kbd>
        </form>
        <div className="docs-nav-pages">
          {DOCS.map((s) => (
            <div key={s.label} className="docs-nav-section">
              <h4>{s.label}</h4>
              {s.pages.map((p) => (
                <a
                  key={p.slug}
                  href={`#/docs/${p.slug}`}
                  className={p === page ? 'is-on' : ''}
                  aria-current={p === page ? 'page' : undefined}
                  onClick={() => setQuery('')}
                >
                  {p.title}
                </a>
              ))}
            </div>
          ))}
        </div>
      </nav>

      <article className="docs-article" ref={article}>
        {searching ? (
          <>
            <div className="docs-eyebrow">Search</div>
            <h1>{hits.length === 0 ? `Nothing for “${q}”` : `${hits.length} result${hits.length === 1 ? '' : 's'} for “${q}”`}</h1>
            {hits.length === 0 ? (
              <p className="docs-lead">Try another word, or pick a page on the left.</p>
            ) : (
              <ol className="docs-hits">
                {hits.map((h) => (
                  <li key={`${h.entry.slug}#${h.entry.heading?.id ?? ''}`}>
                    <a href={`#/docs/${h.entry.slug}`} onClick={(e) => open(e, h)}>
                      <small>
                        {SECTION_OF.get(h.entry.slug)} › {h.entry.title}
                      </small>
                      <b>{h.entry.heading?.text ?? h.entry.title}</b>
                      <span>{h.snippet.map((s, j) => (s.hit ? <mark key={j}>{s.text}</mark> : s.text))}</span>
                    </a>
                  </li>
                ))}
              </ol>
            )}
          </>
        ) : (
          <>
            <div className="docs-eyebrow">{SECTION_OF.get(page.slug)}</div>
            <h1>{page.title}</h1>
            <p className="docs-lead">{page.lead}</p>
            {page.body}
            <footer className="docs-pager">
              {prev ? (
                <a href={`#/docs/${prev.slug}`}>
                  <small>Previous</small>
                  {prev.title}
                </a>
              ) : (
                <span />
              )}
              {next && (
                <a href={`#/docs/${next.slug}`} className="next">
                  <small>Next</small>
                  {next.title}
                </a>
              )}
            </footer>
          </>
        )}
      </article>

      <aside className="docs-toc">
        {!searching && toc.length > 0 && (
          <>
            <h4>On this page</h4>
            {toc.map((t) => (
              <button key={t.id} className="link" onClick={() => document.getElementById(t.id)?.scrollIntoView({ behavior: 'smooth', block: 'start' })}>
                {t.text}
              </button>
            ))}
          </>
        )}
      </aside>
    </div>
  );
}
